/**
 * Reclaim Performance — Golf
 * 
 * Minimal. Premium. Immersive.
 * Flow: Entry → Role (Player/Caddie) → Mode (Signature/Custom) → Focus/Session
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Sparkles, Play, Pause, User, Users,
  SlidersHorizontal, Lock, Info, X,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

const stagger = {
  animate: { transition: { staggerChildren: 0.1 } },
};

const slideUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

// ─── Entry Screen ────────────────────────────────
const EntryScreen = ({ onBegin, onPerformanceVision }) => (
  <motion.div
    key="entry"
    className="min-h-screen flex flex-col items-center justify-center px-8 relative"
    style={{ background: "#0a0f0a" }}
    {...fadeIn}
    transition={{ duration: 1.5 }}
    data-testid="performance-entry"
  >
    <motion.div
      className="absolute w-[500px] h-[300px] rounded-full pointer-events-none"
      style={{ background: "radial-gradient(ellipse, rgba(34,85,34,0.06) 0%, transparent 65%)", filter: "blur(60px)" }}
      animate={{ opacity: [0.3, 0.5, 0.3], scale: [1, 1.03, 1] }}
      transition={{ duration: 10, repeat: 999, repeatType: "loop" }}
    />
    <motion.div className="text-center relative z-10" variants={stagger} initial="initial" animate="animate">
      <motion.p variants={slideUp} className="text-[10px] uppercase tracking-[0.4em] mb-6" style={{ color: "rgba(180,160,120,0.5)" }}>
        Reclaim Performance
      </motion.p>
      <motion.h1 variants={slideUp} className="text-4xl mb-4" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.9)" }}>
        Your Golf Vision
      </motion.h1>
      <motion.p variants={slideUp} className="text-sm italic mb-16" style={{ fontFamily: "'Spectral', serif", color: "rgba(180,160,120,0.35)" }}>
        See it. Feel it. Step into it.
      </motion.p>
      <motion.button
        variants={slideUp}
        onClick={onBegin}
        className="px-10 py-3.5 rounded-full text-sm tracking-wide transition-all"
        style={{
          background: "linear-gradient(135deg, rgba(34,85,34,0.3), rgba(180,160,120,0.15))",
          border: "1px solid rgba(180,160,120,0.2)",
          color: "rgba(245,240,230,0.7)",
          fontFamily: "'Playfair Display', serif",
        }}
        whileTap={{ scale: 0.97 }}
        data-testid="performance-begin-btn"
      >
        Begin
      </motion.button>

      {/* Living Creation Engine — generate a personal performance image */}
      <motion.button
        variants={slideUp}
        onClick={onPerformanceVision}
        className="mt-4 px-6 py-2.5 rounded-full text-[11px] uppercase tracking-[0.32em] transition-all"
        style={{
          background: "transparent",
          border: "1px solid rgba(203,161,83,0.28)",
          color: "rgba(203,161,83,0.78)",
          fontFamily: "'Manrope', sans-serif",
        }}
        whileTap={{ scale: 0.97 }}
        whileHover={{ background: "rgba(203,161,83,0.06)" }}
        data-testid="performance-vision-cta"
      >
        ✦ Create my performance vision
      </motion.button>
    </motion.div>
  </motion.div>
);

// ─── Role Selection ──────────────────────────────
const RoleSelection = ({ onSelectRole, onBack }) => (
  <motion.div
    key="role-select"
    className="min-h-screen flex flex-col p-8 relative"
    style={{ background: "#0a0f0a" }}
    {...fadeIn}
    transition={{ duration: 0.8 }}
    data-testid="role-selection"
  >
    <div className="absolute inset-0 pointer-events-none" style={{
      background: "radial-gradient(ellipse at 50% 30%, rgba(34,85,34,0.04) 0%, transparent 60%)",
    }} />
    <button onClick={onBack} className="relative z-10 mb-12 self-start" style={{ color: "rgba(245,240,230,0.3)" }}>
      <ArrowLeft size={22} />
    </button>
    <motion.div className="relative z-10 max-w-lg mx-auto w-full" variants={stagger} initial="initial" animate="animate">
      <motion.p variants={slideUp} className="text-[10px] uppercase tracking-[0.3em] mb-4" style={{ color: "rgba(180,160,120,0.4)" }}>
        Golf Performance
      </motion.p>
      <motion.h2 variants={slideUp} className="text-2xl mb-2" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.85)" }}>
        Choose your role
      </motion.h2>
      <motion.p variants={slideUp} className="text-sm italic mb-12" style={{ fontFamily: "'Spectral', serif", color: "rgba(180,160,120,0.3)" }}>
        Same environment. Different identity.
      </motion.p>
      <div className="space-y-4">
        {[
          { id: "player", name: "Player", sub: "Step into your best game", Icon: User, tint: "34,85,34" },
          { id: "caddie", name: "Caddie", sub: "Calm. Clear. Steady presence.", Icon: Users, tint: "180,160,120" },
        ].map(({ id, name, sub, Icon, tint }) => (
          <motion.button key={id} variants={slideUp} onClick={() => onSelectRole(id)}
            className="w-full text-left rounded-2xl transition-all overflow-hidden relative"
            style={{ background: `linear-gradient(145deg, rgba(${tint},0.1) 0%, rgba(10,15,10,0.95) 100%)`, border: `1px solid rgba(${tint},0.15)` }}
            whileTap={{ scale: 0.98 }} data-testid={`role-${id}-btn`}
          >
            <div className="p-6 flex items-center gap-5">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `rgba(${tint},0.12)` }}>
                <Icon size={22} style={{ color: `rgba(${tint},0.6)` }} />
              </div>
              <div>
                <h3 className="text-lg mb-0.5" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.85)" }}>{name}</h3>
                <p className="text-[12px] italic" style={{ fontFamily: "'Spectral', serif", color: "rgba(180,160,120,0.35)" }}>{sub}</p>
              </div>
            </div>
          </motion.button>
        ))}
      </div>
    </motion.div>
  </motion.div>
);

// ─── How This Works — Full Screen ────────────────
const HowThisWorks = ({ onClose, onTrySession }) => {
  const sections = [
    { text: "This is not just music." },
    { text: "Each session is carefully structured to guide you into a specific performance state." },
    { label: "The Environment", lines: ["A curated sound environment", "", "The music is designed to support your nervous system.", "", "Not to distract you…", "but to create space, calm, and focus."] },
    { label: "The Arc", lines: ["A structured emotional journey", "", "Each session follows a natural progression:", "", "settle → release → clarity → embodiment → integration", "", "You don't force the state.", "You move into it."] },
    { label: "The Language", lines: ["Precisely placed affirmations", "", "Affirmations are not played continuously.", "", "They are placed at specific moments —", "allowing them to land naturally.", "", "Like thoughts… already there."] },
    { label: "The Subtle Layer", lines: ["Reinforcement beneath awareness", "", "A second layer runs quietly beneath the music.", "", "Not something you focus on…", "but something your system absorbs."] },
    { label: "The Experience", lines: ["Effortless, not forced", "", "You don't need to think.", "You don't need to try.", "", "Just listen…", "and allow the state to settle."] },
    { closing: true, text: "The goal isn't to perform better.\n\nIt's to find yourself already in the state." },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8 }}
      className="fixed inset-0 z-50 overflow-y-auto"
      style={{ background: "#0a0f0a" }}
      data-testid="how-this-works-page"
    >
      {/* Atmospheric background */}
      <div className="fixed inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse at 50% 30%, rgba(34,85,34,0.04) 0%, transparent 50%)",
      }} />
      <div className="fixed inset-0 pointer-events-none opacity-[0.025]" style={{
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 512 512\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.7\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
        backgroundSize: "256px 256px",
      }} />

      {/* Close */}
      <div className="relative z-10 p-6">
        <button onClick={onClose} style={{ color: "rgba(245,240,230,0.25)" }} data-testid="htw-close-btn">
          <X size={22} />
        </button>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-md mx-auto px-8 pb-24">
        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 1 }}
          className="text-2xl mb-16"
          style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.8)" }}
        >
          How this works
        </motion.h1>

        {sections.map((section, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 + idx * 0.15, duration: 0.8 }}
            className={section.closing ? "mt-20 mb-16" : section.label ? "mb-14" : "mb-10"}
          >
            {section.label && (
              <p className="text-[10px] uppercase tracking-[0.25em] mb-4"
                style={{ color: "rgba(180,160,120,0.35)" }}>
                {section.label}
              </p>
            )}

            {section.lines ? (
              section.lines.map((line, li) =>
                line === "" ? (
                  <div key={li} className="h-3" />
                ) : (
                  <p key={li} className="text-[14px] leading-[1.9]"
                    style={{ fontFamily: "'Spectral', serif", color: "rgba(245,240,230,0.4)" }}>
                    {line}
                  </p>
                )
              )
            ) : section.closing ? (
              <p className="text-base leading-[2] italic text-center"
                style={{ fontFamily: "'Spectral', serif", color: "rgba(245,240,230,0.5)", whiteSpace: "pre-line" }}>
                {section.text}
              </p>
            ) : (
              <p className="text-[15px] leading-[1.9]"
                style={{ fontFamily: "'Spectral', serif", fontStyle: "italic", color: "rgba(245,240,230,0.45)" }}>
                {section.text}
              </p>
            )}
          </motion.div>
        ))}

        {/* Try a session button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="text-center"
        >
          <button
            onClick={onTrySession}
            className="px-8 py-3 rounded-full text-sm transition-all"
            style={{
              background: "rgba(251,191,36,0.08)",
              border: "1px solid rgba(251,191,36,0.15)",
              color: "rgba(251,191,36,0.5)",
              fontFamily: "'Playfair Display', serif",
            }}
            data-testid="htw-try-session-btn"
          >
            Try a session
          </button>
        </motion.div>
      </div>
    </motion.div>
  );
};

// ─── Mode Selection (Signature / Custom) ─────────
const ModeSelection = ({ role, arc, isPremium, onSelectSignature, onSelectCustom, onBack }) => {
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const isCaddie = role === "caddie";

  return (
    <motion.div
      key="mode-select"
      className="min-h-screen flex flex-col p-8 relative"
      style={{ background: "#0a0f0a" }}
      {...fadeIn}
      transition={{ duration: 0.8 }}
      data-testid="mode-selection"
    >
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse at 50% 25%, rgba(251,191,36,0.03) 0%, transparent 55%)",
      }} />

      <button onClick={onBack} className="relative z-10 mb-10 self-start" style={{ color: "rgba(245,240,230,0.3)" }}>
        <ArrowLeft size={22} />
      </button>

      <motion.div className="relative z-10 max-w-lg mx-auto w-full" variants={stagger} initial="initial" animate="animate">
        <motion.p variants={slideUp} className="text-[10px] uppercase tracking-[0.25em] mb-3" style={{ color: "rgba(180,160,120,0.4)" }}>
          {isCaddie ? "Caddie" : "Player"}
        </motion.p>
        <motion.h2 variants={slideUp} className="text-2xl mb-2" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.85)" }}>
          Choose your mode
        </motion.h2>

        <div className="space-y-4 mt-8">
          {/* Signature */}
          <motion.button
            variants={slideUp}
            onClick={onSelectSignature}
            className="w-full text-left relative overflow-hidden rounded-2xl transition-all"
            style={{
              background: "linear-gradient(145deg, rgba(251,191,36,0.1) 0%, rgba(10,12,8,0.95) 50%, rgba(251,191,36,0.04) 100%)",
              border: "1px solid rgba(251,191,36,0.2)",
              boxShadow: "0 4px 40px rgba(251,191,36,0.06), 0 0 60px rgba(251,191,36,0.02) inset",
            }}
            whileTap={{ scale: 0.98 }}
            data-testid="perf-signature-btn"
          >
            <motion.div
              className="absolute -top-8 -right-8 w-40 h-40 rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(251,191,36,0.06) 0%, transparent 70%)" }}
              animate={{ opacity: [0.3, 0.6, 0.3], scale: [1, 1.05, 1] }}
              transition={{ duration: 5, repeat: 999, repeatType: "loop" }}
            />
            <div className="relative z-10 p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: "radial-gradient(circle, rgba(251,191,36,0.18) 0%, rgba(251,191,36,0.04) 100%)" }}>
                  <Sparkles size={22} style={{ color: "#FBBF24" }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Signature</h3>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full"
                      style={{ background: "rgba(251,191,36,0.12)", color: "rgba(251,191,36,0.7)", border: "1px solid rgba(251,191,36,0.15)" }}>
                      Recommended
                    </span>
                  </div>
                  <p className="text-sm mb-3" style={{ fontFamily: "'Spectral', serif", fontStyle: "italic", color: "rgba(255,255,255,0.4)" }}>
                    Your fastest way into this state
                  </p>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowHowItWorks(true); }}
                    className="flex items-center gap-1.5 transition-opacity hover:opacity-80"
                    data-testid="perf-how-it-works-btn"
                  >
                    <Info size={11} style={{ color: "rgba(251,191,36,0.35)" }} />
                    <span className="text-[10px]" style={{ color: "rgba(251,191,36,0.35)" }}>How this works</span>
                  </button>
                </div>
              </div>
              <div className="flex justify-end mt-3">
                <motion.div className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: "rgba(251,191,36,0.12)", border: "1px solid rgba(251,191,36,0.2)" }}
                  animate={{ boxShadow: ["0 0 10px rgba(251,191,36,0.05)", "0 0 20px rgba(251,191,36,0.12)", "0 0 10px rgba(251,191,36,0.05)"] }}
                  transition={{ duration: 3, repeat: 999, repeatType: "loop" }}>
                  <Play size={14} className="ml-0.5" style={{ color: "#FBBF24" }} />
                </motion.div>
              </div>
            </div>
          </motion.button>

          {/* Custom — Premium only */}
          <motion.button
            variants={slideUp}
            onClick={isPremium ? onSelectCustom : undefined}
            disabled={!isPremium}
            className="w-full text-left rounded-2xl transition-all relative"
            style={{
              background: "rgba(255,255,255,0.03)",
              border: `1px solid ${isPremium ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)"}`,
              opacity: isPremium ? 1 : 0.5,
            }}
            whileTap={isPremium ? { scale: 0.98 } : {}}
            data-testid="perf-custom-btn"
          >
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(255,255,255,0.05)" }}>
                  {isPremium ? (
                    <SlidersHorizontal size={20} style={{ color: "rgba(255,255,255,0.35)" }} />
                  ) : (
                    <Lock size={18} style={{ color: "rgba(255,255,255,0.2)" }} />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-base" style={{ fontFamily: "'Playfair Display', serif", color: isPremium ? "rgba(255,255,255,0.8)" : "rgba(255,255,255,0.4)" }}>
                      Custom
                    </h3>
                    {!isPremium && (
                      <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full"
                        style={{ background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.25)", border: "1px solid rgba(255,255,255,0.06)" }}>
                        Premium
                      </span>
                    )}
                  </div>
                  <p className="text-sm" style={{ fontFamily: "'Spectral', serif", fontStyle: "italic", color: "rgba(255,255,255,0.25)" }}>
                    Choose your own music, voice & affirmations
                  </p>
                </div>
              </div>
            </div>
          </motion.button>
        </div>
      </motion.div>

      {/* Full-screen How This Works */}
      <AnimatePresence>
        {showHowItWorks && (
          <HowThisWorks
            onClose={() => setShowHowItWorks(false)}
            onTrySession={() => { setShowHowItWorks(false); onSelectSignature(); }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ─── Focus Area Selection (Player Signature) ─────
const FocusAreaSelection = ({ focusAreas, onSelectArea, onBack }) => {
  const [ritualEnabled, setRitualEnabled] = useState(false);
  const [arriving, setArriving] = useState(false);

  const handleFocusClick = (fa) => {
    if (!fa.has_audio) return;
    setArriving(true);
    setTimeout(() => {
      setArriving(false);
      onSelectArea(fa, ritualEnabled);
    }, 3500);
  };

  return (
    <>
      {/* Arrival transition overlay */}
      <AnimatePresence>
        {arriving && (
          <motion.div
            key="arrival"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ background: "#0a0f0a" }}
            data-testid="arrival-transition"
          >
            <motion.div className="text-center">
              <motion.div className="w-24 h-24 rounded-full mx-auto mb-8"
                style={{ background: "radial-gradient(circle, rgba(251,191,36,0.06) 0%, transparent 70%)" }}
                animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 3, repeat: 999, repeatType: "loop" }}
              />
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 2 }}
                className="text-lg italic" style={{ fontFamily: "'Spectral', serif", color: "rgba(245,240,230,0.35)" }}>
                Arrive…
              </motion.p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        key="focus-select"
        className="min-h-screen flex flex-col p-8 relative"
        style={{ background: "#0a0f0a" }}
        {...fadeIn}
        transition={{ duration: 0.8 }}
        data-testid="focus-area-selection"
      >
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(ellipse at 50% 25%, rgba(34,85,34,0.04) 0%, transparent 55%)",
        }} />

        <button onClick={onBack} className="relative z-10 mb-10 self-start" style={{ color: "rgba(245,240,230,0.3)" }}>
          <ArrowLeft size={22} />
        </button>

        <motion.div className="relative z-10 max-w-lg mx-auto w-full" variants={stagger} initial="initial" animate="animate">
          <motion.div variants={slideUp} className="flex items-center gap-2 mb-3">
            <Sparkles size={14} style={{ color: "rgba(251,191,36,0.5)" }} />
            <p className="text-[10px] uppercase tracking-[0.25em]" style={{ color: "rgba(251,191,36,0.5)" }}>Signature</p>
          </motion.div>

          <motion.h2 variants={slideUp} className="text-2xl mb-2" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.85)" }}>
            What's your focus?
          </motion.h2>
          <motion.p variants={slideUp} className="text-sm italic mb-6" style={{ fontFamily: "'Spectral', serif", color: "rgba(180,160,120,0.25)" }}>
            Same state. Different intention.
          </motion.p>

          {/* Ritual toggle */}
          <motion.div variants={slideUp}
            className="flex items-center justify-between rounded-xl px-4 py-3 mb-6"
            style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.04)" }}>
            <span className="text-[12px]" style={{ color: "rgba(255,255,255,0.3)" }}>Add a quick pre-round ritual?</span>
            <button
              onClick={() => setRitualEnabled(!ritualEnabled)}
              className="w-10 h-5 rounded-full transition-all relative"
              style={{ background: ritualEnabled ? "rgba(251,191,36,0.25)" : "rgba(255,255,255,0.08)" }}
              data-testid="perf-ritual-toggle"
            >
              <motion.div className="absolute top-0.5 w-4 h-4 rounded-full"
                style={{ background: ritualEnabled ? "rgba(251,191,36,0.7)" : "rgba(255,255,255,0.3)" }}
                animate={{ left: ritualEnabled ? 22 : 2 }} transition={{ duration: 0.2 }}
              />
            </button>
          </motion.div>

          <div className="space-y-3">
            {focusAreas.map((fa) => (
              <motion.button key={fa.id} variants={slideUp}
                onClick={() => handleFocusClick(fa)}
                disabled={!fa.has_audio || arriving}
                className="w-full text-left rounded-2xl transition-all"
                style={{
                  background: fa.has_audio ? "linear-gradient(145deg, rgba(34,85,34,0.06) 0%, rgba(10,15,10,0.9) 100%)" : "rgba(255,255,255,0.02)",
                  border: `1px solid ${fa.has_audio ? "rgba(34,85,34,0.12)" : "rgba(255,255,255,0.03)"}`,
                  opacity: fa.has_audio ? 1 : 0.35,
                }}
                whileTap={fa.has_audio ? { scale: 0.98 } : {}}
                data-testid={`perf-focus-${fa.id}`}
              >
                <div className="p-5 flex items-center justify-between">
                  <div>
                    <h3 className="text-sm mb-0.5" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.8)" }}>{fa.name}</h3>
                    <p className="text-[11px] italic" style={{ fontFamily: "'Spectral', serif", color: "rgba(180,160,120,0.25)" }}>{fa.subtitle}</p>
                  </div>
                  {fa.has_audio && <Play size={14} style={{ color: "rgba(120,180,120,0.35)" }} />}
                </div>
              </motion.button>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </>
  );
};

// ─── Session Player ──────────────────────────────
const SessionPlayer = ({ roleName, focusName, audioUrl, arc, onBack, onDone }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const isCaddie = roleName === "Caddie";

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTime = () => setCurrentTime(audio.currentTime);
    const onLoaded = () => setDuration(audio.duration);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onLoaded);
    audio.play().catch(() => {});
    return () => {
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.pause();
    };
  }, [audioUrl]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) audioRef.current.pause();
    else audioRef.current.play().catch(() => {});
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, "0")}`;
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const title = focusName || roleName;
  const accent = isCaddie ? "180,160,120" : "34,85,34";

  return (
    <motion.div key="session" className="min-h-screen flex flex-col relative" style={{ background: "#0a0f0a" }}
      {...fadeIn} transition={{ duration: 1 }} data-testid="session-player">

      <motion.div className="absolute pointer-events-none"
        style={{ width: "120%", height: "40%", left: "-10%", top: "15%",
          background: `radial-gradient(ellipse, rgba(${accent},0.05) 0%, transparent 60%)`, filter: "blur(50px)" }}
        animate={{ opacity: [0.3, 0.6, 0.3] }} transition={{ duration: 8, repeat: 999, repeatType: "loop" }}
      />
      <div className="fixed inset-0 pointer-events-none opacity-[0.03]" style={{
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 512 512\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.75\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
        backgroundSize: "256px 256px",
      }} />

      <div className="relative z-10 p-6 flex items-center justify-between">
        <button onClick={onBack} style={{ color: "rgba(245,240,230,0.3)" }} data-testid="session-back-btn"><ArrowLeft size={22} /></button>
        <span className="text-[9px] uppercase tracking-[0.3em]" style={{ color: `rgba(${accent},0.3)` }}>
          {isCaddie ? "Caddie" : "Player"} • Signature
        </span>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-8 relative z-10">
        <motion.div className="relative w-40 h-40 mb-10">
          <motion.div className="absolute inset-0 rounded-full"
            style={{ background: `radial-gradient(circle, rgba(${accent},0.1) 0%, transparent 70%)` }}
            animate={{ scale: [1, 1.08, 1], opacity: [0.4, 0.7, 0.4] }} transition={{ duration: 6, repeat: 999, repeatType: "loop" }}
          />
          <motion.div className="absolute inset-8 rounded-full"
            style={{ background: `radial-gradient(circle, rgba(${accent},0.08) 0%, transparent 70%)` }}
            animate={{ scale: [1.1, 1, 1.1], opacity: [0.3, 0.5, 0.3] }} transition={{ duration: 4, repeat: 999, repeatType: "loop" }}
          />
          <button onClick={togglePlay} className="absolute inset-0 flex items-center justify-center" data-testid="session-play-pause">
            {isPlaying
              ? <Pause size={32} style={{ color: `rgba(${accent},0.4)` }} />
              : <Play size={32} className="ml-1" style={{ color: `rgba(${accent},0.4)` }} />
            }
          </button>
        </motion.div>

        <h2 className="text-xl mb-2 text-center" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.85)" }}>{title}</h2>
        {focusName && <p className="text-[11px] italic mb-2 text-center" style={{ fontFamily: "'Spectral', serif", color: "rgba(180,160,120,0.3)" }}>Golf Performance</p>}

        {arc && (
          <div className="flex items-center gap-2 mb-8">
            {arc.map((phase, i) => (
              <span key={i} className="flex items-center gap-2">
                <span className="text-[9px] capitalize" style={{ color: "rgba(180,160,120,0.2)" }}>{phase}</span>
                {i < arc.length - 1 && <span className="text-[7px]" style={{ color: "rgba(255,255,255,0.06)" }}>→</span>}
              </span>
            ))}
          </div>
        )}

        <div className="w-full max-w-sm mb-3">
          <div className="h-[2px] rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.05)" }}>
            <motion.div className="h-full rounded-full" style={{ width: `${progress}%`, background: `linear-gradient(90deg, rgba(${accent},0.4), rgba(${accent},0.6))` }} />
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-[10px]" style={{ color: "rgba(255,255,255,0.15)" }}>{fmt(currentTime)}</span>
            <span className="text-[10px]" style={{ color: "rgba(255,255,255,0.15)" }}>{fmt(duration)}</span>
          </div>
        </div>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 3 }}
          className="text-[11px] italic text-center mt-6 max-w-xs"
          style={{ fontFamily: "'Spectral', serif", color: "rgba(180,160,120,0.2)" }}>
          {isCaddie ? "Thoughts already happening…" : "The affirmations are beneath conscious perception. Let the music guide your state."}
        </motion.p>

        <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }}
          onClick={onDone} className="mt-10 px-8 py-3 rounded-full text-sm transition-all"
          style={{ border: `1px solid rgba(${accent},0.15)`, color: "rgba(245,240,230,0.35)" }}
          data-testid="session-done-btn">Done</motion.button>
      </div>

      <audio ref={audioRef} src={`${API_URL}${audioUrl}`} preload="auto" data-testid="session-audio" />
    </motion.div>
  );
};

// ─── Main Page ───────────────────────────────────
const GolfPerformancePage = () => {
  const navigate = useNavigate();
  const [screen, setScreen] = useState("entry");
  const [data, setData] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [sessionAudioUrl, setSessionAudioUrl] = useState(null);
  const [sessionFocusName, setSessionFocusName] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/performance/golf`)
      .then((r) => r.json())
      .then(setData)
      .catch(console.error);

    // Check premium status
    const token = localStorage.getItem("reclaim_token");
    if (token) {
      fetch(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.json())
        .then((user) => setIsPremium(user.membership_tier === "premium" || user.membership_tier === "inner_circle"))
        .catch(() => {});
    }
  }, []);

  const handleRoleSelect = (roleId) => {
    setSelectedRole(roleId);
    setScreen("mode"); // Go to Signature/Custom selector
  };

  const handleSignatureSelect = () => {
    if (selectedRole === "caddie") {
      // Caddie has no focus areas — go straight to session via arrival
      const caddieRole = data?.roles?.find((r) => r.id === "caddie");
      if (caddieRole?.has_audio) {
        setSessionAudioUrl(caddieRole.audio_url);
        setSessionFocusName(null);
        setScreen("arrive-session");
      }
    } else {
      setScreen("focus"); // Player picks focus area
    }
  };

  const handleCustomSelect = () => {
    // Navigate to Private Suite with performance mode pre-selected
    navigate("/private-suite");
  };

  const handleFocusSelect = (fa) => {
    setSessionAudioUrl(fa.audio_url);
    setSessionFocusName(fa.name);
    // Arrival happens inside FocusAreaSelection, then calls this
    setScreen("session");
  };

  // For caddie arrival transition
  useEffect(() => {
    if (screen === "arrive-session") {
      const timer = setTimeout(() => setScreen("session"), 3500);
      return () => clearTimeout(timer);
    }
  }, [screen]);

  const playerRole = data?.roles?.find((r) => r.id === "player");
  const arc = data?.arc;

  return (
    <div className="min-h-screen" style={{ background: "#0a0f0a" }}>
      <AnimatePresence mode="wait">
        {screen === "entry" && (
          <EntryScreen
            key="entry"
            onBegin={() => setScreen("role")}
            onPerformanceVision={() => navigate("/performance-vision")}
          />
        )}

        {screen === "role" && (
          <RoleSelection key="role" onSelectRole={handleRoleSelect} onBack={() => setScreen("entry")} />
        )}

        {screen === "mode" && (
          <ModeSelection
            key="mode"
            role={selectedRole}
            arc={arc}
            isPremium={isPremium}
            onSelectSignature={handleSignatureSelect}
            onSelectCustom={handleCustomSelect}
            onBack={() => { setSelectedRole(null); setScreen("role"); }}
          />
        )}

        {screen === "focus" && playerRole && (
          <FocusAreaSelection
            key="focus"
            focusAreas={playerRole.focus_areas || []}
            onSelectArea={(fa) => handleFocusSelect(fa)}
            onBack={() => setScreen("mode")}
          />
        )}

        {screen === "arrive-session" && (
          <motion.div key="arrive" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }} className="min-h-screen flex items-center justify-center"
            style={{ background: "#0a0f0a" }} data-testid="arrival-transition">
            <motion.div className="text-center">
              <motion.div className="w-24 h-24 rounded-full mx-auto mb-8"
                style={{ background: "radial-gradient(circle, rgba(180,160,120,0.06) 0%, transparent 70%)" }}
                animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 3, repeat: 999, repeatType: "loop" }}
              />
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 2 }}
                className="text-lg italic" style={{ fontFamily: "'Spectral', serif", color: "rgba(245,240,230,0.35)" }}>
                Arrive…
              </motion.p>
            </motion.div>
          </motion.div>
        )}

        {screen === "session" && sessionAudioUrl && (
          <SessionPlayer
            key="session"
            roleName={selectedRole === "caddie" ? "Caddie" : "Player"}
            focusName={sessionFocusName}
            audioUrl={sessionAudioUrl}
            arc={arc}
            onBack={() => {
              setSessionAudioUrl(null);
              setScreen(selectedRole === "caddie" ? "mode" : "focus");
            }}
            onDone={() => {
              setSessionAudioUrl(null);
              setScreen(selectedRole === "caddie" ? "mode" : "focus");
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default GolfPerformancePage;
